import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { DIVIDER, TEXT_SOFT } from './FlightCheckParts.js';
import type { TrendPoint } from './flightCheckMocks.js';
import { formatDayMonth, formatScore, MAX_SCORE, STRONG_FROM, scoreText } from './scoreScale.js';

const HEIGHT = 170;
const AXIS_WIDTH = 36;
const TOP = 12;
const PX_PER_POINT = 26;
const AXIS_MIN = 5;
const EDGE_PAD = 0.13;
const TICKS = [MAX_SCORE, STRONG_FROM, AXIS_MIN] as const;

const yOf = (score: number): number =>
  TOP + (MAX_SCORE - Math.min(Math.max(score, AXIS_MIN), MAX_SCORE)) * PX_PER_POINT;

// x as a share of the plot width, so the SVG stretches without distorting the points.
const xOf = (index: number, count: number): string =>
  count === 1 ? '50%' : `${(EDGE_PAD + ((1 - 2 * EDGE_PAD) * index) / (count - 1)) * 100}%`;

export function ScoreTrendChart({ points }: { points: TrendPoint[] }): ReactNode {
  const theme = useTheme();
  const line = theme.palette.primary.main;
  const labelStyle = { fontSize: 11, fill: TEXT_SOFT, fontFamily: 'inherit' };

  return (
    <Box sx={{ position: 'relative', height: HEIGHT, width: '100%' }}>
      {TICKS.map((tick) => (
        <Typography
          key={tick}
          sx={{ position: 'absolute', left: 0, top: yOf(tick) - 8, fontSize: 11, color: TEXT_SOFT }}
        >
          {tick}
        </Typography>
      ))}
      <Box sx={{ position: 'absolute', left: AXIS_WIDTH, right: 0, top: 0, bottom: 0 }}>
        <svg width="100%" height={HEIGHT} role="img" aria-label="Average score by batch">
          <rect
            x="0"
            y={yOf(MAX_SCORE)}
            width="100%"
            height={yOf(STRONG_FROM) - yOf(MAX_SCORE)}
            fill={theme.palette.score.high.bg}
          />
          {TICKS.map((tick) => (
            <line key={tick} x1="0" x2="100%" y1={yOf(tick)} y2={yOf(tick)} stroke={DIVIDER} />
          ))}
          <text
            x="100%"
            dx={-8}
            y={yOf(MAX_SCORE) + 15}
            textAnchor="end"
            style={{ ...labelStyle, fill: theme.palette.score.high.text, fontWeight: 500 }}
          >
            Strong 8+
          </text>
          {points.slice(1).map((point, index) => (
            <line
              key={point.ranAt}
              x1={xOf(index, points.length)}
              y1={yOf(points[index]?.score ?? point.score)}
              x2={xOf(index + 1, points.length)}
              y2={yOf(point.score)}
              stroke={line}
              strokeWidth={2}
            />
          ))}
          {points.map((point, index) => (
            <g key={point.ranAt} data-testid="trend-point">
              <circle
                cx={xOf(index, points.length)}
                cy={yOf(point.score)}
                r={5}
                fill={theme.palette.background.paper}
                stroke={line}
                strokeWidth={2}
              />
              <text
                x={xOf(index, points.length)}
                y={yOf(point.score) - 12}
                textAnchor="middle"
                style={{
                  ...labelStyle,
                  fontSize: 13,
                  fontWeight: 600,
                  fill: scoreText(theme, point.score),
                }}
              >
                {formatScore(point.score)}
              </text>
              <text x={xOf(index, points.length)} y={167} textAnchor="middle" style={labelStyle}>
                {formatDayMonth(point.ranAt)}
              </text>
            </g>
          ))}
        </svg>
      </Box>
    </Box>
  );
}
